import {
  AnalyzeResultEventTypeSummary,
  AnalyzeResultProcessType,
  PicklistEventType,
} from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getCurrencySymbolSettings } from '../../common/formatHelper';
import useFormatter from '../../common/useFormatter';
import { cn } from '../../common/utils';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import DropdownSelector from '../../components/actions/DropdownSelector';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import StatItem from '../../components/stats/StatItem';
import { warehouseSelected } from '../../store/warehouse.state';
import { LCV_EU_Multiplier } from '../store/resourcePolicy.default';
import {
  AnalyzeResultType,
  EventGroup,
  EventGroupGlobal,
  EventGroupKeys,
  EventGroupMap,
  KPI,
  getEventGroupColor,
  getEventGroupTitle,
} from '../store/simulation.helper';
import { EventsDetailsPanelBaseProps } from './AnalyzeEventsDetailsPanel';
import StackedBar from './StackedBar';

export type OptimisationEventsCompareProps = EventsDetailsPanelBaseProps & {
  eventsBefore: AnalyzeResultEventTypeSummary[];
  eventsAfter: AnalyzeResultEventTypeSummary[];
  energyRate: number;
};

const chartMinHeight = 300;

export type EventGroupGlobalValues = Record<EventGroupGlobal, number>;
export type EventGroupValues = Record<EventGroup, number>;
export type EventGroupExtendedValues = EventGroupValues & {
  idle: number;
  overwork: number;
  total: number;
};

export type CompareRawDatum = {
  analyzeType: AnalyzeResultType;
} & EventGroupValues;

const OptimisationEventsComparePanel: React.FC<
  OptimisationEventsCompareProps
> = props => {
  const { t } = useTranslation('simulation');
  const wh = useRecoilValue(warehouseSelected);

  const [kpi, setKpi] = useState<KPI>('cost');
  const [isFullStatMode, setIsFullStatMode] = useState(false);
  const formatter = useFormatter();

  const KPIs: KPI[] = ['cost', 'duration', 'co2', 'share'];

  function getFormatter(kpi: KPI) {
    switch (kpi) {
      case 'cost':
        return formatter.formatCurrency;
      case 'duration':
        return formatter.formatTimeSpan;
      case 'co2':
        return formatter.formatCO2;
      case 'share':
        return formatter.formatShare;
      default:
        return formatter.formatCurrency;
    }
  }

  function getEventKPIValue(event: AnalyzeResultEventTypeSummary, kpi: KPI) {
    switch (kpi) {
      case 'cost':
        return event.totalCost;
      case 'duration':
        return event.totalDuration;
      case 'co2':
        return event.eventType === PicklistEventType.TRAVELLING_HORIZONTAL ||
          event.eventType === PicklistEventType.TRAVELLING_VERTICAL
          ? event.totalDuration * props.energyRate * LCV_EU_Multiplier
          : 0;
      case 'share':
        return (
          event.totalDuration / _.sumBy(props.eventsBefore, 'totalDuration')
        );
      default:
        return 0;
    }
  }

  const eventsGroupsBefore: EventGroupValues = _.reduce(
    _.filter(
      props.eventsBefore,
      e => e.processType === AnalyzeResultProcessType.PICKING,
    ),
    (acc, event) => {
      return {
        ...acc,
        [EventGroupMap[event.eventType]]:
          (acc[EventGroupMap[event.eventType]] ?? 0) +
          getEventKPIValue(event, kpi),
      };
    },
    {} as EventGroupValues,
  );

  const eventsGroupsAfter: EventGroupValues = _.reduce(
    _.filter(
      props.eventsAfter,
      e => e.processType === AnalyzeResultProcessType.PICKING,
    ),
    (acc, event) => {
      return {
        ...acc,
        [EventGroupMap[event.eventType]]:
          (acc[EventGroupMap[event.eventType]] ?? 0) +
          getEventKPIValue(event, kpi),
      };
    },
    {} as EventGroupValues,
  );

  const combinedDatum: CompareRawDatum[] = [
    {
      analyzeType: 'before',
      ...eventsGroupsBefore,
    },
    {
      analyzeType: 'after',
      ...eventsGroupsAfter,
    },
  ];

  const formatFn = getFormatter(kpi);

  const kpiSwitch = props.showKpiSwitch ? (
    <ButtonSwitchMulti
      autoSize
      options={[
        {
          label: t`Cost`,
          icon: getCurrencySymbolSettings(wh.currency).symbol,
        },
        {
          label: t`Time`,
          icon: 'h',
        },
        {
          label: t`CO2`,
          icon: 'CO2',
        },
        {
          label: t`Share`,
          icon: '%',
        },
      ]}
      selectedIndex={KPIs.indexOf(kpi)}
      onClick={index => {
        setKpi(KPIs[index]);
      }}
      buttonType="minimal"
    />
  ) : null;

  return (
    <StatGroup
      title={props.title}
      subTitle={t`Stats`}
      helpNavTo={'simulation/optimise/simulation-optimise-summary-events'}
      titleAction={
        !props.dashboardSimulationView && (
          <DropdownSelector
            classNameLabel="text-sm"
            onClick={e => {
              e.stopPropagation();
            }}
            DropAlignRight
            buttonTransparent
            vertical
            panelMode
            value={'...'}
            values={[isFullStatMode ? t`Show Minimal` : t`Show Compare`]} // values={options}
            onChange={async (option, e) => {
              setIsFullStatMode(!isFullStatMode);
            }}
          />
        )
      }
    >
      <Stat
        hasHelper
        isPreview
        inPanelMode
        cardView={props.cardView}
        // titleElement={
        //   <div className="flex items-center justify-between">
        //     <ButtonSwitchMulti
        //       autoSize
        //       options={[
        //         {
        //           label: t`Detailed`,
        //           // icon: 'list',
        //         },
        //         {
        //           label: t`Short`,
        //           // icon: 'chart-bar',
        //         },
        //       ]}
        //       selectedIndex={isFullStatMode ? 1 : 0}
        //       onClick={index => setIsFullStatMode(index === 1)}
        //       buttonType="minimal"
        //     />
        //   </div>
        // }
        switchUnitOfMeasure={kpiSwitch}
      >
        <div
          data-component="StackedBarContainer"
          className={cn(
            'relative',
            'flex flex-1 flex-col items-center',
            'p-0.5 md:p-1 xl:p-2',
          )}
        >
          <div
            data-component="StackedBarWrapper"
            style={{ height: chartMinHeight, minWidth: '100%' }}
          >
            <StackedBar data={combinedDatum} valueFormatter={formatFn} />
          </div>
        </div>

        {props.showLegendStats && (
          <div className="gap-2 text-xs">
            {_.map(EventGroupKeys, key => {
              const beforeValue = eventsGroupsBefore?.[key] ?? 0;
              const afterValue = eventsGroupsAfter?.[key] ?? 0;

              // Only show if either before or after values are greater than 0
              if (beforeValue === 0 && afterValue === 0) return null;

              const diff = afterValue - beforeValue;
              const percentageDiff =
                beforeValue !== 0 ? (diff / beforeValue) * 100 : 0;
              const isImprovement = diff < 0;

              return (
                <div key={`event-group-${key}`}>
                  {isFullStatMode ? (
                    <Stat
                      className="mb-10 !p-1"
                      inPanelMode
                      isPreview
                      titleElement={
                        <div
                          className="flex w-full items-center border-b pb-2"
                          style={{
                            borderColor: getEventGroupColor(key),
                          }}
                        >
                          <div
                            data-component="StatItemLegend"
                            className={classNames(
                              'ltr:mr-2 rtl:ml-2',
                              'h-3 w-3 lg:h-4 lg:w-4',
                              'rounded-full',
                            )}
                            style={{
                              borderColor: getEventGroupColor(key),
                              background: getEventGroupColor(key),
                            }}
                          />
                          <div className="w-full text-sm">
                            {getEventGroupTitle(key, t)}
                          </div>
                        </div>
                      }
                      valueDiffPercent={percentageDiff.toFixed(1)}
                      valueDiff={formatFn(Math.abs(diff)).value}
                      valueBefore={formatFn(beforeValue)}
                      valueAfter={formatFn(afterValue)}
                      isComparable
                      isPrimaryResult
                      unitOfMeasure={formatFn(afterValue).unit}
                      isUnitPrefix={formatFn(afterValue).prefixUnit}
                      valueIsBig
                      isFullWidth
                    />
                  ) : (
                    <>
                      <StatItem
                        title={getEventGroupTitle(key, t)}
                        value={formatFn(beforeValue)}
                        valueToCompare={formatFn(afterValue)}
                        legendColor={getEventGroupColor(key)}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Stat>
    </StatGroup>
  );
};

export default OptimisationEventsComparePanel;
